import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location])

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/story', label: 'Story' },
    { path: '/directory', label: 'Directory' },
    { path: '/recipes', label: 'Recipes' },
    { path: '/history', label: 'History' },
    { path: '/submit', label: 'Submit' },
  ]

  const isActive = (path: string) => location.pathname === path

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isMobileMenuOpen
          ? 'bg-rich-mahogany/95 backdrop-blur-sm shadow-lg'
          : 'bg-transparent'
      }`}
      role="banner"
    >
      <nav
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center space-x-3 group"
            aria-label="THE BLACK PLATE - Home"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-terracotta to-burnt-sienna rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <span className="text-warm-cream font-crimson font-bold text-xl">BP</span>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-warm-cream font-crimson font-bold text-xl leading-tight">
                THE BLACK PLATE
              </h1>
              <p className="text-golden-amber text-xs tracking-wide">
                Soul Food History & Directory
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg font-inter text-sm font-medium transition-all duration-200 ${
                  isActive(link.path)
                    ? 'bg-terracotta text-warm-cream'
                    : 'text-warm-cream/90 hover:bg-warm-cream/10 hover:text-warm-cream'
                }`}
                aria-current={isActive(link.path) ? 'page' : undefined}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg text-warm-cream hover:bg-warm-cream/10 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <i
              className={`fas ${isMobileMenuOpen ? 'fa-times' : 'fa-bars'} text-xl`}
              aria-hidden="true"
            ></i>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div
            id="mobile-menu"
            className="lg:hidden pb-4 mobile-menu-enter"
          >
            <div className="flex flex-col space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 rounded-lg font-inter text-base font-medium transition-all ${
                    isActive(link.path)
                      ? 'bg-terracotta text-warm-cream'
                      : 'text-warm-cream/90 hover:bg-warm-cream/10'
                  }`}
                  aria-current={isActive(link.path) ? 'page' : undefined}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  )
}

export default Header
